import { useCallback, useEffect, useRef, useState } from 'react';
import {
  contentRect,
  toNormalized,
  toClient,
  distanceToPoint,
  type ContentRect,
  type NormPoint,
} from './roiGeometry';
import {
  directionLabels,
  arrowTip,
  isDrawable,
  GRAB_THRESHOLD_PX,
  MAX_LINES_PER_FEED,
  type Line,
} from './lineGeometry';

/**
 * Draws counting lines over the live video.
 *
 * A line is two points in normalised (0..1) frame coordinates. The backend tracks people
 * across frames and counts each one that crosses it, by direction — see backend/flow_tracker.py
 * and backend/flow_counter.py. Directions are named LEFT/RIGHT (or UP/DOWN) from the line's own
 * geometry, never stored; the database keeps the original in/out.
 *
 * Sibling of RoiEditor, and deliberately built the same way: same wrapper measurement, same SVG
 * overlay, same toolbar. Only meaningful over the server-composited MJPEG feed, which is the
 * surface the counters are burned into.
 */

/** The RTMP reader's output size (main.py, out_w/out_h) — used until the <img> has decoded. */
const FALLBACK_FRAME = { width: 640, height: 360 };

const LINE_COLOR = '#78dcff';
const SELECTED_COLOR = '#ffd166';
const IN_COLOR = '#8cff8c';

export interface TripwireEditorProps {
  /** The `.video-wrapper` the video and this overlay share. */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
  onClose: () => void;
  /** Told how many lines this feed now has, so the panel can light its button. */
  onLinesChange?: (count: number) => void;
}

interface Gate {
  id: number;
  name: string;
  line_px: number[][];
  orientation: number;
  stream_key: string | null;
}

const asLine = (g: Gate): Line => [
  [g.line_px[0][0], g.line_px[0][1]],
  [g.line_px[1][0], g.line_px[1][1]],
];

export default function TripwireEditor({
  wrapperRef, apiUrl, authHeaders, onClose, onLinesChange,
}: TripwireEditorProps) {
  const [streamKey, setStreamKey] = useState<string | null>(null);
  const [gates, setGates] = useState<Gate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<NormPoint[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  /** Which endpoint of which saved line is being dragged. */
  const drag = useRef<{ id: number; end: 0 | 1 } | null>(null);
  const dragMoved = useRef(false);

  // --- keep the overlay glued to the picture, not to the element -------- //
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    const natW = img?.naturalWidth || FALLBACK_FRAME.width;
    const natH = img?.naturalHeight || FALLBACK_FRAME.height;
    setRect(contentRect(wrapper.clientWidth, wrapper.clientHeight, natW, natH));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    // The <img> reports naturalWidth only once a frame has decoded, which is after mount.
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- load this camera's lines ----------------------------------------- //
  const load = useCallback(async (key: string) => {
    const res = await fetch(
      `${apiUrl}/api/analytics/gates?scope=group&stream_key=${encodeURIComponent(key)}`,
      { headers: authHeaders() });
    if (!res.ok) throw new Error(`Could not load counting lines (${res.status})`);
    const rows: Gate[] = await res.json();
    const usable = rows.filter(g => Array.isArray(g.line_px) && g.line_px.length === 2);
    setGates(usable);
    onLinesChange?.(usable.length);
    return usable;
  }, [apiUrl, authHeaders, onLinesChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The stream key names the camera. Asked for rather than guessed: the format
        // ('demo', 'rtmp:<url>', 'file:<path>') is the backend's business, not the UI's.
        const res = await fetch(`${apiUrl}/api/drone/video-source`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Could not identify the camera (${res.status})`);
        const key = (await res.json()).processing_stream_key;
        if (cancelled) return;
        if (!key) { setStatus('Select a video source first'); return; }
        setStreamKey(key);
        await load(key);
      } catch (e) {
        if (!cancelled) setStatus(e instanceof Error ? e.message : 'Could not reach the backend');
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders, load]);

  // --- drawing ---------------------------------------------------------- //
  const localPoint = (e: React.PointerEvent | React.MouseEvent): [number, number] => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return [0, 0];
    return [e.clientX - box.left, e.clientY - box.top];
  };

  const startNew = () => {
    if (gates.length >= MAX_LINES_PER_FEED) {
      setStatus(`A camera can carry at most ${MAX_LINES_PER_FEED} counting lines`);
      return;
    }
    setSelectedId(null);
    setDraft([]);
    setDrawing(true);
    setStatus(null);
  };

  const save = useCallback(async (line: Line) => {
    if (!streamKey) return;
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${apiUrl}/api/analytics/gates?scope=group`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          name: `Counting line ${gates.length + 1}`,
          line_px: [[line[0][0], line[0][1]], [line[1][0], line[1][1]]],
          stream_key: streamKey,
          orientation: 1,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setStatus((body && body.detail) || `Save failed (${res.status})`);
        return;
      }
      const created: Gate = await res.json();
      setDraft([]);
      setDrawing(false);
      setSelectedId(created.id);
      await load(streamKey);
    } catch {
      setStatus('Save failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  }, [apiUrl, authHeaders, gates.length, load, streamKey]);

  const handleClick = (e: React.MouseEvent) => {
    if (dragMoved.current) return;
    const [x, y] = localPoint(e);
    if (!drawing) {
      // Click near an endpoint of a saved line selects it; anywhere else clears the selection.
      const hit = gates.find(g =>
        asLine(g).some(p => distanceToPoint(p, x, y, rect) <= GRAB_THRESHOLD_PX * 2));
      setSelectedId(hit ? hit.id : null);
      return;
    }
    const p = toNormalized(x, y, rect);
    if (draft.length === 0) { setDraft([p]); return; }
    const line: Line = [draft[0], p];
    if (!isDrawable(line)) { setStatus('The two ends must be different points'); return; }
    save(line);
  };

  const persistEnds = useCallback(async (gate: Gate) => {
    try {
      const res = await fetch(`${apiUrl}/api/analytics/gates/${gate.id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ line_px: gate.line_px }),
      });
      if (!res.ok) setStatus(`Move failed (${res.status})`);
    } catch {
      setStatus('Move failed — backend unreachable');
    }
  }, [apiUrl, authHeaders]);

  const handleMove = (e: React.PointerEvent) => {
    const [x, y] = localPoint(e);
    if (drag.current) {
      dragMoved.current = true;
      const { id, end } = drag.current;
      const next = toNormalized(x, y, rect);
      setGates(prev => prev.map(g => (g.id === id
        ? { ...g, line_px: end === 0 ? [[next[0], next[1]], g.line_px[1]]
                                     : [g.line_px[0], [next[0], next[1]]] }
        : g)));
      return;
    }
    if (drawing) setCursor([x, y]);
  };

  const startDrag = (id: number, end: 0 | 1) => (e: React.PointerEvent) => {
    if (drawing) return;
    e.stopPropagation();
    drag.current = { id, end };
    dragMoved.current = false;
    setSelectedId(id);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const endDrag = () => {
    const held = drag.current;
    drag.current = null;
    if (held && dragMoved.current) {
      const gate = gates.find(g => g.id === held.id);
      if (gate) persistEnds(gate);
    }
    // Let the click that follows a real drag be swallowed, but not a plain click.
    setTimeout(() => { dragMoved.current = false; }, 0);
  };

  const selected = gates.find(g => g.id === selectedId) || null;

  const flip = useCallback(async () => {
    if (!selected || !streamKey) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/analytics/gates/${selected.id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ orientation: selected.orientation >= 0 ? -1 : 1 }),
      });
      if (!res.ok) { setStatus(`Flip failed (${res.status})`); return; }
      await load(streamKey);
    } catch {
      setStatus('Flip failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  }, [apiUrl, authHeaders, load, selected, streamKey]);

  const remove = useCallback(async () => {
    if (!selected || !streamKey) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/analytics/gates/${selected.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok && res.status !== 404) { setStatus(`Delete failed (${res.status})`); return; }
      setSelectedId(null);
      await load(streamKey);
    } catch {
      setStatus('Delete failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  }, [apiUrl, authHeaders, load, selected, streamKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (drawing) { setDrawing(false); setDraft([]); setCursor(null); }
        else onClose();
      }
      if (e.key === 'Backspace' && drawing && draft.length) setDraft([]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawing, draft.length, onClose]);

  // --- render ----------------------------------------------------------- //
  const btn = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 3, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
    letterSpacing: '0.04em', ...extra,
  });

  const renderLine = (line: Line, orientation: number, key: string, color: string,
                      onGrab?: (end: 0 | 1) => (e: React.PointerEvent) => void) => {
    const [x1, y1] = toClient(line[0][0], line[0][1], rect);
    const [x2, y2] = toClient(line[1][0], line[1][1], rect);
    const [mx, my] = [(x1 + x2) / 2, (y1 + y2) / 2];
    const [ax, ay] = arrowTip(line, orientation, rect);
    const [inLabel] = directionLabels(line, orientation);
    return (
      <g key={key}>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={2} pointerEvents="none" />
        {/* The arrow is the only thing that says which side counts as the first direction —
            without it "flip" is a guess, which is what it used to be. */}
        <line x1={mx} y1={my} x2={ax} y2={ay} stroke={IN_COLOR} strokeWidth={2}
              markerEnd="url(#tw-arrow)" pointerEvents="none" />
        <text x={ax + 6} y={ay + 4} fill={IN_COLOR} fontSize={11} pointerEvents="none">
          {inLabel}
        </text>
        {[0, 1].map(i => (
          <circle
            key={i} cx={i === 0 ? x1 : x2} cy={i === 0 ? y1 : y2} r={6}
            fill="rgba(0,0,0,0.6)" stroke={color} strokeWidth={2}
            style={{ cursor: onGrab ? 'move' : 'default' }}
            onPointerDown={onGrab?.(i as 0 | 1)}
          />
        ))}
      </g>
    );
  };

  const draftLine: Line | null = drawing && draft.length === 1 && cursor
    ? [draft[0], toNormalized(cursor[0], cursor[1], rect)]
    : null;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
      <svg
        ref={svgRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: drawing ? 'crosshair' : 'default', touchAction: 'none',
        }}
        onClick={handleClick}
        onPointerMove={handleMove}
        onPointerUp={endDrag}
        onPointerLeave={() => { endDrag(); setCursor(null); }}
      >
        <defs>
          <marker id="tw-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={IN_COLOR} />
          </marker>
        </defs>

        {gates.map(g => renderLine(
          asLine(g), g.orientation, `g${g.id}`,
          g.id === selectedId ? SELECTED_COLOR : LINE_COLOR,
          end => startDrag(g.id, end),
        ))}

        {draftLine && renderLine(draftLine, 1, 'draft', SELECTED_COLOR)}
        {drawing && draft.length === 1 && !cursor && (() => {
          const [dx, dy] = toClient(draft[0][0], draft[0][1], rect);
          return <circle cx={dx} cy={dy} r={6} fill={SELECTED_COLOR} pointerEvents="none" />;
        })()}
      </svg>

      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6,
        alignItems: 'center', flexWrap: 'wrap', maxWidth: 'calc(100% - 16px)',
      }}>
        <span style={{
          background: 'rgba(0,0,0,0.65)', color: LINE_COLOR, fontSize: 11,
          padding: '3px 8px', borderRadius: 3, letterSpacing: '0.05em',
        }}>
          {drawing
            ? draft.length === 0
              ? 'COUNTING LINE — click where the line starts'
              : 'COUNTING LINE — click where it ends'
            : selected
              ? `${selected.name.toUpperCase()} — drag an end, or FLIP the direction`
              : `COUNTING LINES (${gates.length}) — click a line to select it`}
        </span>
        {!drawing && gates.length < MAX_LINES_PER_FEED && (
          <button style={btn({ borderColor: LINE_COLOR, color: LINE_COLOR })}
                  onClick={startNew} disabled={busy || !streamKey}>NEW LINE</button>
        )}
        {!drawing && selected && (
          <>
            <button style={btn()} onClick={flip} disabled={busy}>FLIP</button>
            <button style={btn()} onClick={remove} disabled={busy}>DELETE</button>
          </>
        )}
        {drawing && (
          <button style={btn()} onClick={() => { setDrawing(false); setDraft([]); setCursor(null); }}>
            STOP DRAWING
          </button>
        )}
        <button style={btn()} onClick={onClose}>CLOSE</button>
        {status && (
          <span style={{
            background: 'rgba(120,0,0,0.75)', color: '#ffd7d7', fontSize: 11,
            padding: '3px 8px', borderRadius: 3,
          }}>{status}</span>
        )}
      </div>
    </div>
  );
}
